import { API_BASE_URL } from "../../config/api";
import { transformTheme } from "../models/Theme";
import { transformUser } from "../models/User";
import { transformQuestion } from "../models/Question";
import { transformAnswer, transformAnswerToApi } from "../models/Answer";
import { ApiClient } from "../api/ApiClient";
import { StateManager } from "../state/StateManager";
import { ApiError } from "../errors/ApiError";
import { logger } from "../utils/logger";
import { USER_ROLES } from "../config/constants";

/**
 * Data source with professional error handling, caching, and state management
 * @param {Object} auth - Authentication object with token and user
 * @returns {Object} Data source API and state
 */
export function createDataSource(auth) {
  const { token, user } = auth;
  
  // Debug logging
  logger.info("Creating data source", { 
    hasToken: !!token, 
    tokenLength: token?.length, 
    hasUser: !!user, 
    userId: user?.id,
    apiBaseUrl: API_BASE_URL || "not set"
  });
  
  // Initialize API client and state manager
  const apiClient = new ApiClient({
    baseURL: API_BASE_URL || "",
    timeout: 15000,
    retries: 3
  });
  
  const stateManager = new StateManager();
  
  // Current language state (mutable)
  let currentLanguage = null;
  
  // Cache keys
  const CACHE_KEYS = {
    THEMES: 'themes',
    USERS: 'users',
    QUESTIONS: 'questions',
    RESPONSES: 'responses'
  };

  /**
   * Get user's common language with fallback
   */
  const getUserLanguage = () => user?.commonLanguage || "fr";

  /**
   * Get current language for theme operations (UI language takes precedence)
   */
  const getCurrentLanguage = () => {
    const language = currentLanguage || getUserLanguage();
    if (!language) {
      logger.warn("No language available, falling back to 'fr'");
      return "fr";
    }
    return language;
  };

  /**
   * Update the current language and invalidate theme cache
   */
  const updateLanguage = (newLanguage) => {
    if (newLanguage !== currentLanguage) {
      currentLanguage = newLanguage;
      // Invalidate all theme caches when language changes
      stateManager.invalidateAll();
    }
  };

  /**
   * Theme operations
   */
  const themeOperations = {
    async list(requestedLanguage = null) {
      try {
        // Use requested language, current UI language, or fall back to user's common language
        const languageToUse = requestedLanguage || getCurrentLanguage();
        
        // Create language-specific cache key to ensure themes are reloaded when language changes
        const languageSpecificCacheKey = `${CACHE_KEYS.THEMES}_${languageToUse}`;
        
        return stateManager.get(languageSpecificCacheKey, async () => {
          logger.info("Loading themes from API", { language: languageToUse });
          const apiThemes = await apiClient.call("/theme/all", {}, token);
          const transformedThemes = apiThemes.reduce((themes,theme) => {
            const transformedTheme = transformTheme(theme, languageToUse)
            if (transformedTheme) themes.push(transformedTheme);
            return themes;
          },[]);

          logger.info("Themes loaded and transformed", { count: transformedThemes.length, language: languageToUse });
          return transformedThemes;
        });
      } catch (error) {
        logger.error("Failed to load themes", { error: error.message, requestedLanguage, status: error.status });
        // If it's a 401, provide a more helpful error message
        if (error.status === 401) {
          throw new ApiError(`Authentication failed. Please check if the API server is running and your token is valid.`, 401, 'Unauthorized');
        }
        throw new ApiError(`Failed to load themes: ${error.message}`, error.status, error.statusText);
      }
    },

    async create(payload) {
      try {
        const apiPayload = { name: payload.nom, language: payload.contentLanguageCode };
        const newTheme = await apiClient.call("/theme", {
          method: "POST",
          body: JSON.stringify(apiPayload)
        }, token);

        // Invalidate only theme-related caches to force refresh
        stateManager.invalidate(CACHE_KEYS.THEMES);
        // Also invalidate language-specific theme caches
        const languageSpecificCacheKey = `${CACHE_KEYS.THEMES}_${getCurrentLanguage()}`;
        stateManager.invalidate(languageSpecificCacheKey);
        
        // If API returns empty response, return null (will be handled by cache refresh)
        if (!newTheme || Object.keys(newTheme).length === 0) {
          return null;
        }

        // Transform and return the new theme
        const languageToUse = getCurrentLanguage();
        return transformTheme(newTheme, languageToUse);
      } catch (error) {
        throw new ApiError(`Failed to create theme: ${error.message}`, error.status, error.statusText);
      }
    },

    async update(id, payload) {
      try {
        const params = new URLSearchParams({
          id: id
        });

        const apiPayload = {
          name: payload.nom,
          language: getCurrentLanguage()
        };
        
        // Include userId if provided (optional)
        if (payload.userId) {
          apiPayload.userId = payload.userId;
        }

        await apiClient.call(`/theme?${params}`, {
          method: "PATCH",
          body: JSON.stringify(apiPayload)
        }, token);

        // Invalidate only theme-related caches to force refresh
        stateManager.invalidate(CACHE_KEYS.THEMES);
        // Also invalidate language-specific theme caches
        const languageSpecificCacheKey = `${CACHE_KEYS.THEMES}_${getCurrentLanguage()}`;
        stateManager.invalidate(languageSpecificCacheKey);
        
        // Reload themes to get updated data
        const updatedThemes = await themeOperations.list(getCurrentLanguage());
        const updatedTheme = updatedThemes.find(t => t.id === id);
        
        return updatedTheme || null;
      } catch (error) {
        logger.error("Failed to update theme", { error: error.message, id, payload });
        throw new ApiError(`Failed to update theme: ${error.message}`, error.status, error.statusText);
      }
    },

    async softDelete(id, withChildren = false) {
      try {
        const params = new URLSearchParams({
          id: id,
          withChildren: withChildren.toString()
        });

        await apiClient.call(`/theme?${params}`, {
          method: "DELETE"
        }, token);

        // Invalidate only theme-related caches to force refresh
        stateManager.invalidate(CACHE_KEYS.THEMES);
        // Also invalidate language-specific theme caches
        const languageSpecificCacheKey = `${CACHE_KEYS.THEMES}_${getCurrentLanguage()}`;
        stateManager.invalidate(languageSpecificCacheKey);
        
        return { success: true };
      } catch (error) {
        throw new ApiError(`Failed to delete theme: ${error.message}`, error.status, error.statusText);
      }
    },

    async restore(id, withChildren = false) {
      try {
        const params = new URLSearchParams({
          id: id,
          withChildren: withChildren.toString()
        });

        await apiClient.call(`/theme/restore?${params}`, {
          method: "POST"
        }, token);

        // Invalidate only theme-related caches to force refresh
        stateManager.invalidate(CACHE_KEYS.THEMES);
        // Also invalidate language-specific theme caches
        const languageSpecificCacheKey = `${CACHE_KEYS.THEMES}_${getCurrentLanguage()}`;
        stateManager.invalidate(languageSpecificCacheKey);
        
        return { success: true };
      } catch (error) {
        throw new ApiError(`Failed to restore theme: ${error.message}`, error.status, error.statusText);
      }
    },

    async hardDelete(id) {
      // TODO: Implement when API endpoint is available
      throw new ApiError("Hard delete theme not yet implemented", 501, "Not Implemented");
    }
  };

  /**
   * User operations
   */
  const userOperations = {
    async list() {
      try {
        // Only fetch users if the current user is an admin
        if (user?.role !== USER_ROLES.ADMIN) {
          logger.info("Skipping user fetch - current user is not admin", { userRole: user?.role });
          return [];
        }

        return stateManager.get(CACHE_KEYS.USERS, async () => {
          logger.info("Loading users from API", { hasToken: !!token, tokenLength: token?.length, userRole: user?.role });
          const apiUsers = await apiClient.call("/user/all", {}, token);
          const transformedUsers = apiUsers.map(user => transformUser(user));
          logger.info("Users loaded and transformed", { count: transformedUsers.length });
          return transformedUsers;
        });
      } catch (error) {
        logger.error("Failed to load users", { error: error.message, hasToken: !!token, status: error.status, userRole: user?.role });
        // If it's a 401, provide a more helpful error message
        if (error.status === 401) {
          throw new ApiError(`Authentication failed. Please check if the API server is running and your token is valid.`, 401, 'Unauthorized');
        }
        throw new ApiError(`Failed to load users: ${error.message}`, error.status, error.statusText);
      }
    },

    async create(payload) {
      try {
        // Transform the payload to match API requirements
        const apiPayload = {
          firstName: payload.firstName,
          lastName: payload.name, // The form uses 'name' for lastName
          email: payload.email,
          password: payload.password,
          commonLanguageCode: payload.commonLanguage || "en",
          culturalLanguage: payload.culturalLanguage || "",
          role: payload.role || USER_ROLES.ADMIN
        };

        logger.info("Creating user via API", { email: apiPayload.email, role: apiPayload.role });
        
        const newUser = await apiClient.call("/user", {
          method: "POST",
          body: JSON.stringify(apiPayload)
        }, token);

        // Invalidate users cache to force refresh
        stateManager.invalidate(CACHE_KEYS.USERS);
        
        logger.info("User created successfully", { email: apiPayload.email });
        return newUser;
      } catch (error) {
        logger.error("Failed to create user", { error: error.message, payload });
        throw new ApiError(`Failed to create user: ${error.message}`, error.status, error.statusText);
      }
    },

    async update(id, payload) {
      try {
        const params = new URLSearchParams({
          id: id
        });

        // Only send provided fields so callers (e.g. profile) can update a subset
        const apiPayload = {
          firstName: payload.firstName,
          lastName: payload.name, // The form uses 'name' for lastName
          email: payload.email,
          commonLanguageCode: payload.commonLanguage,
          culturalLanguage: payload.culturalLanguage,
          role: payload.role,
          password: payload.password || undefined
        };
        Object.keys(apiPayload).forEach(key => apiPayload[key] === undefined && delete apiPayload[key]);

        logger.info("Updating user via API", { id, fields: Object.keys(apiPayload).filter(key => key !== 'password') });

        await apiClient.call(`/user?${params}`, {
          method: "PATCH",
          body: JSON.stringify(apiPayload)
        }, token);

        // Invalidate users cache to force refresh
        stateManager.invalidate(CACHE_KEYS.USERS);

        return { success: true };
      } catch (error) {
        logger.error("Failed to update user", { error: error.message, id });
        throw new ApiError(`Failed to update user: ${error.message}`, error.status, error.statusText);
      }
    },

    async softDelete(id) {
      throw new ApiError("Soft delete user not yet implemented", 501, "Not Implemented");
    },

    async restore(id) {
      throw new ApiError("Restore user not yet implemented", 501, "Not Implemented");
    },

    async hardDelete(id) {
      throw new ApiError("Hard delete user not yet implemented", 501, "Not Implemented");
    }
  };

  /**
   * Question operations
   */
  const questionOperations = {
    async list(requestedLanguage = null) {
      try {
        // Use requested language, current UI language, or fall back to user's common language
        const languageToUse = requestedLanguage || getCurrentLanguage();
        
        // Create language-specific cache key to ensure questions are reloaded when language changes
        const languageSpecificCacheKey = `${CACHE_KEYS.QUESTIONS}_${languageToUse}`;
        
        return stateManager.get(languageSpecificCacheKey, async () => {
          logger.info("Loading questions from API", { language: languageToUse });
          const apiQuestions = await apiClient.call("/question/all", {}, token);
          const transformedQuestions = apiQuestions.reduce((questions,question) => {
            const transformedQuestion = transformQuestion(question, languageToUse)
            if (transformedQuestion) questions.push(transformedQuestion);
            return questions;
          },[]);

          logger.info("Questions loaded and transformed", { count: transformedQuestions.length, language: languageToUse });
          return transformedQuestions;
        });
      } catch (error) {
        logger.error("Failed to load questions", { error: error.message, requestedLanguage, status: error.status });
        // If it's a 401, provide a more helpful error message
        if (error.status === 401) {
          throw new ApiError(`Authentication failed. Please check if the API server is running and your token is valid.`, 401, 'Unauthorized');
        }
        throw new ApiError(`Failed to load questions: ${error.message}`, error.status, error.statusText);
      }
    },

    async create(payload) {
      try {
        logger.info("Creating new question", { payload });
        
        // Transform payload to API format
        const apiPayload = {
          themeId: payload.themeId,
          questionText: payload.questionText,
          language: payload.contentLanguageCode
        };

        const newQuestion = await apiClient.call("/question", {
          method: "POST",
          body: JSON.stringify(apiPayload)
        }, token);

        // Invalidate only question caches to force refresh
        stateManager.invalidate(CACHE_KEYS.QUESTIONS);
        // Also invalidate language-specific question caches
        const languageSpecificCacheKey = `${CACHE_KEYS.QUESTIONS}_${getCurrentLanguage()}`;
        stateManager.invalidate(languageSpecificCacheKey);
        
        // If API returns empty response, return null (will be handled by cache refresh)
        if (!newQuestion || Object.keys(newQuestion).length === 0) {
          return null;
        }

        // Transform and return the new question
        const languageToUse = getCurrentLanguage();
        return transformQuestion(newQuestion, languageToUse);
      } catch (error) {
        logger.error("Failed to create question", { error: error.message, payload });
        throw new ApiError(`Failed to create question: ${error.message}`, error.status, error.statusText);
      }
    },

    async update(id, payload) {
      try {
        const params = new URLSearchParams({
          id: id
        });

        const apiPayload = {
          questionText: payload.questionText,
          language: getCurrentLanguage()
        };
        
        // Include userId if provided (optional)
        if (payload.userId) {
          apiPayload.userId = payload.userId;
        }

        await apiClient.call(`/question?${params}`, {
          method: "PATCH",
          body: JSON.stringify(apiPayload)
        }, token);

        // Invalidate only question-related caches to force refresh
        stateManager.invalidate(CACHE_KEYS.QUESTIONS);
        // Also invalidate language-specific question caches
        const languageSpecificCacheKey = `${CACHE_KEYS.QUESTIONS}_${getCurrentLanguage()}`;
        stateManager.invalidate(languageSpecificCacheKey);
        
        // Reload questions to get updated data
        const updatedQuestions = await questionOperations.list(getCurrentLanguage());
        const updatedQuestion = updatedQuestions.find(q => q.id === id);
        
        return updatedQuestion || null;
      } catch (error) {
        logger.error("Failed to update question", { error: error.message, id, payload });
        throw new ApiError(`Failed to update question: ${error.message}`, error.status, error.statusText);
      }
    },

    async softDelete(id, withChildren = false) {
      try {
        const params = new URLSearchParams({
          id: id,
          withChildren: withChildren.toString()
        });

        await apiClient.call(`/question?${params}`, {
          method: "DELETE"
        }, token);

        // Invalidate only question-related caches to force refresh
        stateManager.invalidate(CACHE_KEYS.QUESTIONS);
        // Also invalidate language-specific question caches
        const languageSpecificCacheKey = `${CACHE_KEYS.QUESTIONS}_${getCurrentLanguage()}`;
        stateManager.invalidate(languageSpecificCacheKey);
        
        // If withChildren is true, also invalidate response caches since responses may have been deleted
        if (withChildren) {
          stateManager.invalidate(CACHE_KEYS.RESPONSES);
          const responsesCacheKey = `${CACHE_KEYS.RESPONSES}_${getCurrentLanguage()}`;
          stateManager.invalidate(responsesCacheKey);
        }
        
        // Reload questions to get updated data
        await questionOperations.list(getCurrentLanguage());
        
        return true;
      } catch (error) {
        logger.error("Failed to soft delete question", { error: error.message, id, withChildren });
        throw new ApiError(`Failed to soft delete question: ${error.message}`, error.status, error.statusText);
      }
    },

    async restore(id) {
      throw new ApiError("Restore question not yet implemented", 501, "Not Implemented");
    },

    async hardDelete(id) {
      throw new ApiError("Hard delete question not yet implemented", 501, "Not Implemented");
    }
  };

  /**
   * Transform API response data to internal format
   */
  const transformResponse = (apiResponse) => {
    // Use Answer model for consistent transformation
    return transformAnswer(apiResponse, getUserLanguage());
  };

  /**
   * Response operations
   */
  const responseOperations = {
    async list(requestedLanguage = null) {
      try {
        // Use requested language, current UI language, or fall back to user's common language
        const languageToUse = requestedLanguage || getCurrentLanguage();
        
        // Create language-specific cache key to ensure responses are reloaded when language changes
        const languageSpecificCacheKey = `${CACHE_KEYS.RESPONSES}_${languageToUse}`;
        
        return stateManager.get(languageSpecificCacheKey, async () => {
          logger.info("Loading responses from API", { language: languageToUse });
          const apiResponses = await apiClient.call("/answer/all", {}, token);
          const transformedResponses = apiResponses.reduce((responses, response) => {
            const transformedResponse = transformAnswer(response, languageToUse)
            if (transformedResponse) responses.push(transformedResponse);
            return responses
          }, [])

          logger.info("Responses loaded and transformed", { count: transformedResponses.length, language: languageToUse });
          return transformedResponses;
        });
      } catch (error) {
        logger.error("Failed to load responses", { error: error.message, requestedLanguage, status: error.status });
        // If it's a 401, provide a more helpful error message
        if (error.status === 401) {
          throw new ApiError(`Authentication failed. Please check if the API server is running and your token is valid.`, 401, 'Unauthorized');
        }
        throw new ApiError(`Failed to load responses: ${error.message}`, error.status, error.statusText);
      }
    },

    async create(payload) {
      logger.info("Creating new response", { payload });
      
      // Transform payload to API format - use the new simplified structure
      const apiPayload = {
        questionId: payload.questionId,
        answerText: payload.answerText,
        language: payload.contentLanguageCode
      };
      
      const newResponse = await apiClient.call("/answer", {
        method: "POST",
        body: JSON.stringify(apiPayload)
      }, token);

      // Invalidate responses cache to force refresh
      stateManager.invalidate(CACHE_KEYS.RESPONSES);
      // Also invalidate language-specific response caches
      const languageSpecificCacheKey = `${CACHE_KEYS.RESPONSES}_${getCurrentLanguage()}`;
      stateManager.invalidate(languageSpecificCacheKey);

      // If API returns empty response, return null (will be handled by cache refresh)
      if (!newResponse || Object.keys(newResponse).length === 0) {
        return null;
      }

      // Transform and return the new response
      const languageToUse = getCurrentLanguage();
      return transformAnswer(newResponse, languageToUse);
    },

    async update(id, payload) {
      try {
        logger.info("Updating response", { id, payload });
        
        const params = new URLSearchParams({
          id: id
        });

        const apiPayload = {
          answerText: payload.answerText,
          language: getCurrentLanguage()
        };
        
        // Include userId if provided (optional)
        if (payload.userId) {
          apiPayload.userId = payload.userId;
        }
        
        await apiClient.call(`/answer?${params}`, {
          method: "PATCH",
          body: JSON.stringify(apiPayload)
        }, token);

        // Invalidate responses cache to force refresh
        stateManager.invalidate(CACHE_KEYS.RESPONSES);
        // Also invalidate language-specific response caches
        const languageSpecificCacheKey = `${CACHE_KEYS.RESPONSES}_${getCurrentLanguage()}`;
        stateManager.invalidate(languageSpecificCacheKey);
        
        // Reload responses to get updated data
        const updatedResponses = await responseOperations.list(getCurrentLanguage());
        const updatedResponse = updatedResponses.find(r => r.id === id);
        
        return updatedResponse || null;
      } catch (error) {
        logger.error("Failed to update response", { error: error.message, id, payload });
        throw new ApiError(`Failed to update response: ${error.message}`, error.status, error.statusText);
      }
    },

    async softDelete(id) {
      try {
        logger.info("Soft deleting response", { id });
        
        const params = new URLSearchParams({
          id: id
        });

        await apiClient.call(`/answer?${params}`, {
          method: "DELETE"
        }, token);

        // Invalidate responses cache to force refresh
        stateManager.invalidate(CACHE_KEYS.RESPONSES);
        // Also invalidate language-specific response caches
        const languageSpecificCacheKey = `${CACHE_KEYS.RESPONSES}_${getCurrentLanguage()}`;
        stateManager.invalidate(languageSpecificCacheKey);

        // Reload responses to ensure state is up-to-date
        await responseOperations.list(getCurrentLanguage());

        return true;
      } catch (error) {
        logger.error("Failed to soft delete response", { error: error.message, id });
        throw new ApiError(`Failed to soft delete response: ${error.message}`, error.status, error.statusText);
      }
    },

    async restore(id) {
      logger.info("Restoring response", { id });
      await apiClient.call(`/answer/${id}/restore`, {
        method: "POST"
      }, token);

      // Invalidate responses cache to force refresh
      stateManager.invalidate(CACHE_KEYS.RESPONSES);
      // Also invalidate language-specific response caches
      const languageSpecificCacheKey = `${CACHE_KEYS.RESPONSES}_${getCurrentLanguage()}`;
      stateManager.invalidate(languageSpecificCacheKey);
    },

    async hardDelete(id) {
      logger.info("Hard deleting response", { id });
      await apiClient.call(`/answer/${id}/hard`, {
        method: "DELETE"
      }, token);

      // Invalidate responses cache to force refresh
      stateManager.invalidate(CACHE_KEYS.RESPONSES);
      // Also invalidate language-specific response caches
      const languageSpecificCacheKey = `${CACHE_KEYS.RESPONSES}_${getCurrentLanguage()}`;
      stateManager.invalidate(languageSpecificCacheKey);
    }
  };

  /**
   * API interface
   */
  const api = {
    // Theme operations
    async listThemes() {
      // Auto-load themes if not already loaded (backward compatibility)
      const languageToUse = getCurrentLanguage();
      const languageSpecificCacheKey = `${CACHE_KEYS.THEMES}_${languageToUse}`;
      const cachedThemes = stateManager.getCached(languageSpecificCacheKey);
      if (!cachedThemes || cachedThemes.length === 0) {
        return await themeOperations.list();
      }
      return cachedThemes;
    },

    async createTheme(payload) {
      return await themeOperations.create(payload);
    },

    async updateTheme(id, payload) {
      return await themeOperations.update(id, payload);
    },

    async softDeleteTheme(id, withChildren = false) {
      return await themeOperations.softDelete(id, withChildren);
    },

    async restoreTheme(id) {
      return await themeOperations.restore(id);
    },

    async hardDeleteTheme(id) {
      return await themeOperations.hardDelete(id);
    },

    // User operations
    async listUsers() {
      return await userOperations.list();
    },

    async createUser(payload) {
      return await userOperations.create(payload);
    },

    async updateUser(id, payload) {
      return await userOperations.update(id, payload);
    },

    async softDeleteUser(id) {
      return await userOperations.softDelete(id);
    },

    async restoreUser(id) {
      return await userOperations.restore(id);
    },

    async hardDeleteUser(id) {
      return await userOperations.hardDelete(id);
    },

    // Question operations
    async listQuestions(requestedLanguage = null) {
      return await questionOperations.list(requestedLanguage);
    },

    async createQuestion(payload) {
      return await questionOperations.create(payload);
    },

    async updateQuestion(id, payload) {
      return await questionOperations.update(id, payload);
    },

    async softDeleteQuestion(id, withChildren = false) {
      return await questionOperations.softDelete(id, withChildren);
    },

    async restoreQuestion(id) {
      return await questionOperations.restore(id);
    },

    async hardDeleteQuestion(id) {
      return await questionOperations.hardDelete(id);
    },

    // Response operations
    async listResponses(requestedLanguage = null) {
      return await responseOperations.list(requestedLanguage);
    },

    async createResponse(payload) {
      return await responseOperations.create(payload);
    },

    async updateResponse(id, payload) {
      return await responseOperations.update(id, payload);
    },

    async softDeleteResponse(id) {
      return await responseOperations.softDelete(id);
    },

    async restoreResponse(id) {
      return await responseOperations.restore(id);
    },

    async hardDeleteResponse(id) {
      return await responseOperations.hardDelete(id);
    }
  };

  /**
   * State interface with reactive updates
   */
  const state = {
    get themes() {
      // Get themes for the current UI language
      const languageToUse = getCurrentLanguage();
      const languageSpecificCacheKey = `${CACHE_KEYS.THEMES}_${languageToUse}`;
      return stateManager.getCached(languageSpecificCacheKey) || [];
    },
    get users() {
      return stateManager.getCached(CACHE_KEYS.USERS) || [];
    },
    get questions() {
      // Get questions for the current UI language
      const languageToUse = getCurrentLanguage();
      const languageSpecificCacheKey = `${CACHE_KEYS.QUESTIONS}_${languageToUse}`;
      return stateManager.getCached(languageSpecificCacheKey) || [];
    },
    get responses() {
      // Get responses for the current UI language
      const languageToUse = getCurrentLanguage();
      const languageSpecificCacheKey = `${CACHE_KEYS.RESPONSES}_${languageToUse}`;
      return stateManager.getCached(languageSpecificCacheKey) || [];
    }
  };

  /**
   * Reload function with selective cache invalidation
   */
  const reload = async (keys = null, requestedLanguage = null) => {
    try {
      if (keys === null) {
        // Reload all
        stateManager.invalidateAll();
        await Promise.all([
          themeOperations.list(requestedLanguage),
          userOperations.list(),
          questionOperations.list(),
          responseOperations.list()
        ]);
      } else {
        // Reload specific keys
        const reloadPromises = [];
        if (keys.includes('themes')) {
          // Invalidate only theme-related caches
          stateManager.invalidate(CACHE_KEYS.THEMES);
          // Also invalidate language-specific theme caches
          const languageSpecificCacheKey = `${CACHE_KEYS.THEMES}_${requestedLanguage || getUserLanguage()}`;
          stateManager.invalidate(languageSpecificCacheKey);
          reloadPromises.push(themeOperations.list(requestedLanguage));
        }
        if (keys.includes('users')) {
          stateManager.invalidate(CACHE_KEYS.USERS);
          reloadPromises.push(userOperations.list());
        }
        if (keys.includes('questions')) {
          // Invalidate only question-related caches
          stateManager.invalidate(CACHE_KEYS.QUESTIONS);
          // Also invalidate language-specific question caches
          const languageSpecificCacheKey = `${CACHE_KEYS.QUESTIONS}_${requestedLanguage || getUserLanguage()}`;
          stateManager.invalidate(languageSpecificCacheKey);
          reloadPromises.push(questionOperations.list(requestedLanguage));
        }
        if (keys.includes('responses')) {
          // Invalidate only response-related caches
          stateManager.invalidate(CACHE_KEYS.RESPONSES);
          // Also invalidate language-specific response caches
          const languageSpecificCacheKey = `${CACHE_KEYS.RESPONSES}_${requestedLanguage || getUserLanguage()}`;
          stateManager.invalidate(languageSpecificCacheKey);
          reloadPromises.push(responseOperations.list(requestedLanguage));
        }
        await Promise.all(reloadPromises);
      }
    } catch (error) {
      const status = error.status || error.statusCode || 500;
      const statusText = error.statusText || error.message || 'Internal Server Error';
      throw new ApiError(`Failed to reload data: ${error.message}`, status, statusText);
    }
  };

  /**
   * Get loading states for UI
   */
  const getLoadingStates = () => {
    const languageToUse = getCurrentLanguage();
    const themesCacheKey = `${CACHE_KEYS.THEMES}_${languageToUse}`;
    const questionsCacheKey = `${CACHE_KEYS.QUESTIONS}_${languageToUse}`;
    const responsesCacheKey = `${CACHE_KEYS.RESPONSES}_${languageToUse}`;
    return {
      themes: stateManager.isLoading(themesCacheKey),
      users: stateManager.isLoading(CACHE_KEYS.USERS),
      questions: stateManager.isLoading(questionsCacheKey),
      responses: stateManager.isLoading(responsesCacheKey)
    };
  };

  /**
   * Get cache statistics for debugging
   */
  const getCacheStats = () => stateManager.getStats();

  return {
    api,
    state,
    reload,
    getLoadingStates,
    getCacheStats,
    updateLanguage,
    stateManager, // Expose for advanced usage
    apiClient // Expose for advanced usage
  };
}
